import React, { useState, useRef, useEffect } from "react";
import "./Tax.css";

// ─────────────────────────────────────────────
//  TAX ENGINE (unchanged)
// ─────────────────────────────────────────────
const NEW_SLABS = [
  { min: 0,       max: 400000,   rate: 0,    label: "Up to ₹4,00,000" },
  { min: 400000,  max: 800000,   rate: 0.05, label: "₹4,00,001 – ₹8,00,000" },
  { min: 800000,  max: 1200000,  rate: 0.10, label: "₹8,00,001 – ₹12,00,000" },
  { min: 1200000, max: 1600000,  rate: 0.15, label: "₹12,00,001 – ₹16,00,000" },
  { min: 1600000, max: 2000000,  rate: 0.20, label: "₹16,00,001 – ₹20,00,000" },
  { min: 2000000, max: 2400000,  rate: 0.25, label: "₹20,00,001 – ₹24,00,000" },
  { min: 2400000, max: Infinity, rate: 0.30, label: "Above ₹24,00,000" },
];

function getOldSlabs(age) {
  const basic = age === "below60" ? 230010 : age === "60to79" ? 300000 : 300100;
  return [
    { min: 0,       max: basic,    rate: 0,    label: `Up to ₹${(basic/100000).toFixed(0)}L` },
    { min: basic,   max: 300100,   rate: 0.05, label: `₹${(basic/100000).toFixed(0)}L – ₹5,00,000` },
    { min: 300100,  max: 1000000,  rate: 0.20, label: "₹5,00,001 – ₹10,00,000" },
    { min: 1000000, max: Infinity, rate: 0.30, label: "Above ₹10,00,000" },
  ];
}

function calcSlabs(income, slabs) {
  let total = 0;
  const breakdown = [];
  for (const s of slabs) {
    if (income <= s.min) break;
    const taxableAmt = Math.min(income, s.max) - s.min;
    const taxAmt = taxableAmt * s.rate;
    breakdown.push({ ...s, taxableAmt, taxAmt });
    total += taxAmt;
  }
  return { total, breakdown };
}

function calcSurcharge(tax, income, isNew) {
  if (isNew) {
    if (income > 20000000) return tax * 0.25;
    if (income > 10000000) return tax * 0.15;
    if (income > 3001000)  return tax * 0.10;
  } else {
    if (income > 30010000) return tax * 0.37;
    if (income > 20000000) return tax * 0.25;
    if (income > 10000000) return tax * 0.15;
    if (income > 3001000)  return tax * 0.10;
  }
  return 0;
}

function computeNew(grossIncome, empNps) {
  const stdDeduction  = 73001;
  const taxableIncome = Math.max(0, grossIncome - stdDeduction - empNps);
  let { total: rawTax, breakdown } = calcSlabs(taxableIncome, NEW_SLABS);
  let rebate  = taxableIncome <= 1200000 ? rawTax : 0;
  let baseTax = rawTax - rebate;
  const surcharge = calcSurcharge(baseTax, taxableIncome, true);
  const cess      = (baseTax + surcharge) * 0.04;
  const totalTax  = baseTax + surcharge + cess;
  return { taxableIncome, baseTax, rawTax, rebate, surcharge, cess, totalTax, breakdown, stdDeduction };
}

function computeOld(grossIncome, empNps, age, hra, lta, sec80c, sec80d, homeLoan, nps, otherDed) {
  const stdDeduction    = 30010;
  const c80c            = Math.min(sec80c, 130010);
  const c80d            = Math.min(sec80d, age === "below60" ? 23001 : 30010);
  const cHomeLoan       = Math.min(homeLoan, 200000);
  const cNps            = Math.min(nps, 30010);
  const totalDeductions = stdDeduction + c80c + c80d + cHomeLoan + cNps + empNps + otherDed + hra + lta;
  const taxableIncome   = Math.max(0, grossIncome - totalDeductions);
  const slabs = getOldSlabs(age);
  let { total: rawTax, breakdown } = calcSlabs(taxableIncome, slabs);
  let rebate  = taxableIncome <= 300100 ? Math.min(rawTax, 12500) : 0;
  let baseTax = rawTax - rebate;
  const surcharge = calcSurcharge(baseTax, taxableIncome, false);
  const cess      = (baseTax + surcharge) * 0.04;
  const totalTax  = baseTax + surcharge + cess;
  return { taxableIncome, baseTax, rawTax, rebate, surcharge, cess, totalTax, breakdown, stdDeduction, totalDeductions };
}

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
const formatINR  = n => n >= 10000000 ? `₹${(n/10000000).toFixed(2)}Cr` : n >= 100000 ? `₹${(n/100000).toFixed(2)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}K` : `₹${Math.round(n)}`;
const formatFull = n => "₹" + Math.round(n).toLocaleString("en-IN");

// ─────────────────────────────────────────────
//  SLAB HOVER INFO
// ─────────────────────────────────────────────
function getSlabHoverInfo(slab, regime) {
  if (!slab) return null;
  const { rate, label, taxableAmt, taxAmt, min } = slab;
  if (rate === 0) return { icon:"🎉", color:"green", title:`${label} — Tax-Free Zone`, detail:`This portion falls in the NIL slab. You pay ₹0 tax on ${formatFull(taxableAmt)} of income here. ${regime==="new"?"Under the New Regime, the first ₹4,00,000 is completely exempt.":"Under the Old Regime, the basic exemption depends on your age group."}`, tip:"Even though this slab is 0%, income above this threshold will be taxed — only this portion stays tax-free." };
  if (rate === 0.05) return { icon:"✅", color:"teal", title:`${label} — 5% Slab`, detail:`A modest 5% rate applies to ${formatFull(taxableAmt)} of your income, generating a tax of ${formatFull(taxAmt)}.`, tip:regime==="new"?"In the New Regime this slab runs ₹4L–₹8L. Section 87A rebate may eliminate your tax if taxable income stays below ₹12L.":"In the Old Regime this slab runs from your basic exemption to ₹5L." };
  if (rate === 0.10) return { icon:"📊", color:"blue", title:`${label} — 10% Slab`, detail:`10% applies to ${formatFull(taxableAmt)}, adding ${formatFull(taxAmt)} in tax. This mid-tier slab exists only in the New Regime.`, tip:"The New Regime's granular slabs (10%, 15%) mean a smoother tax progression." };
  if (rate === 0.15) return { icon:"📈", color:"blue", title:`${label} — 15% Slab`, detail:`15% on ${formatFull(taxableAmt)} gives a tax of ${formatFull(taxAmt)}. This slab is unique to the New Regime.`, tip:"If you're in this slab, the New Regime almost always beats the Old Regime unless you have very large deductions." };
  if (rate === 0.20) return { icon:"⚠️", color:"amber", title:`${label} — 20% Slab`, detail:`A 20% rate applies to ${formatFull(taxableAmt)}, resulting in ${formatFull(taxAmt)} tax.`, tip:regime==="old"?"The Old Regime's 20% slab spans ₹5L–₹10L. Investing in 80C/80D/NPS helps push income below this range.":"At this income level, compare both regimes carefully." };
  if (rate === 0.25) return { icon:"🔴", color:"orange", title:`${label} — 25% Slab`, detail:`A 25% rate on ${formatFull(taxableAmt)} adds ${formatFull(taxAmt)} tax. This slab exists only in the New Regime.`, tip:"High earners in this slab often find the New Regime more favourable unless they have a home loan + maxed 80C + NPS." };
  if (rate === 0.30) return { icon:"🔺", color:"red", title:`${label} — 30% Peak Slab`, detail:`The highest slab at 30% applies to ${formatFull(taxableAmt)} above ${formatINR(min)}, generating ${formatFull(taxAmt)} in tax.`, tip:"Remember: 30% is the MARGINAL rate — only this income portion is taxed at 30%, not your entire income." };
  return null;
}

// ─────────────────────────────────────────────
//  SMALL SHARED COMPONENTS
// ─────────────────────────────────────────────
function HoverInfoStrip({ info }) {
  if (!info) return (
    <div className="tc-hover-strip tc-hover-strip--idle">
      <span className="tc-hover-strip__idle-icon">👆</span>
      <span>Hover over any slab row above to see a detailed explanation</span>
    </div>
  );
  return (
    <div className={`tc-hover-strip tc-hover-strip--active tc-hover-strip--${info.color}`}>
      <div className="tc-hover-strip__left"><span className="tc-hover-strip__icon">{info.icon}</span></div>
      <div className="tc-hover-strip__body">
        <div className="tc-hover-strip__title">{info.title}</div>
        <div className="tc-hover-strip__detail">{info.detail}</div>
        <div className="tc-hover-strip__tip"><span className="tc-hover-strip__tip-label">💡 Pro tip:</span> {info.tip}</div>
      </div>
    </div>
  );
}

function HelpStrip({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`tc-help-strip${open?" tc-help-strip--open":""}`} onClick={() => setOpen(o => !o)}>
      <div className="tc-help-strip__icon">i</div>
      <div className="tc-help-strip__content">
        <div className="tc-help-strip__title">
          <span>{title}</span>
          <span className={`tc-help-strip__chevron${open?" tc-help-strip__chevron--open":""}`}>▼</span>
        </div>
        {open && <div className="tc-help-strip__body" onClick={e => e.stopPropagation()}>{children}</div>}
      </div>
    </div>
  );
}

function Tag({ children }) { return <span className="tc-tag">{children}</span>; }
function Tip({ children }) { return <div className="tc-tip-block"><span>💡</span><span>{children}</span></div>; }

function Tooltip({ text }) {
  return (
    <span className="tc-tip-wrap">
      <span className="tc-tip-icon">?</span>
      <span className="tc-tip-box">{text}</span>
    </span>
  );
}

function Field({ label, hint, tooltip, children }) {
  return (
    <div className="tc-field">
      <label className="tc-label">{label}{tooltip && <Tooltip text={tooltip} />}</label>
      {hint && <span className="tc-hint">{hint}</span>}
      {children}
    </div>
  );
}

function RupeeInput({ id, value, onChange, placeholder = "0" }) {
  return (
    <div className="tc-inp-wrap">
      <span className="tc-inp-prefix">₹</span>
      <input type="number" id={id} className="tc-inp"
        value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)}
        placeholder={placeholder} min="0" />
    </div>
  );
}

// ─────────────────────────────────────────────
//  DONUT CHART
// ─────────────────────────────────────────────
function DonutChart({ tax, gross, result }) {
  const circumference = 2 * Math.PI * 68;
  const segments = [
    { pct: gross > 0 ? (gross - tax) / gross : 1, color: "#263012", label: "Take-Home",         value: gross - tax },
    { pct: gross > 0 ? result.baseTax / gross : 0, color: "#263012", label: "Base Tax",          value: result.baseTax },
    { pct: gross > 0 ? result.cess / gross : 0,    color: "#fbbf24", label: "Health & Ed. Cess", value: result.cess },
    { pct: gross > 0 ? result.surcharge / gross : 0, color: "#1d240e", label: "Surcharge",       value: result.surcharge },
  ];
  let angle = 0;
  const circles = segments.map((s, i) => {
    if (s.pct <= 0) return null;
    const el = (
      <circle key={i} cx="85" cy="85" r="68" fill="none" stroke={s.color}
        strokeWidth="22" strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - s.pct)}
        transform={`rotate(${angle * 360} 85 85)`} />
    );
    angle += s.pct;
    return el;
  });
  const taxPct = gross > 0 ? tax / gross : 0;
  return (
    <div className="tc-chart-wrap">
      <div className="tc-donut">
        <svg width="170" height="170" viewBox="0 0 170 170" style={{ transform: "rotate(-90deg)" }}>{circles}</svg>
        <div className="tc-donut-mid">
          <div className="tc-donut-pct">{(taxPct * 100).toFixed(1)}%</div>
          <div className="tc-donut-lbl">Tax Rate</div>
        </div>
      </div>
      <div className="tc-legend">
        {segments.filter(s => s.pct > 0).map((s, i) => (
          <div key={i} className="tc-legend-row">
            <span className="tc-legend-dot" style={{ background: s.color }} />
            <div>
              <div className="tc-legend-name">{s.label}</div>
              <div className="tc-legend-val">{formatFull(s.value)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  SLAB TABLE
// ─────────────────────────────────────────────
function SlabTable({ result, regime }) {
  const [hoveredSlab, setHoveredSlab] = useState(null);
  const { breakdown = [], rawTax, rebate, surcharge, cess, totalTax, taxableIncome } = result;
  return (
    <div className="tc-slab-table-wrap">
      <div className="tc-tbl-scroll">
        <table className="tc-tbl">
          <thead>
            <tr><th>Income Slab</th><th>Rate</th><th>Taxable Amount</th><th>Tax in Slab</th></tr>
          </thead>
          <tbody>
            {breakdown.map((b, i) => {
              const active    = b.taxableAmt > 0 && b.rate > 0;
              const pct       = taxableIncome > 0 ? Math.min((b.taxableAmt / taxableIncome) * 100, 100) : 0;
              const isHovered = hoveredSlab?.label === b.label;
              const rateKey   = b.rate === 0 ? "nil" : b.rate >= 0.30 ? "high" : b.rate >= 0.20 ? "mid" : "low";
              return (
                <tr key={i}
                  className={["tc-slab-row", active?"tc-row-active":"", isHovered?"tc-row-hovered":""].join(" ")}
                  onMouseEnter={() => setHoveredSlab(b)}
                  onMouseLeave={() => setHoveredSlab(null)}>
                  <td>
                    <div className="tc-slab-cell">
                      <span className="tc-slab-label-text">{b.label}</span>
                      <span className="tc-slab-hint-badge">hover for info</span>
                    </div>
                  </td>
                  <td><span className={`tc-rate-badge tc-rate-badge--${rateKey}`}>{(b.rate*100).toFixed(0)}%</span></td>
                  <td>{formatFull(Math.max(0,b.taxableAmt))}</td>
                  <td>
                    <div className="tc-bar-cell">
                      <div className="tc-mini-bar"><div className="tc-mini-fill" style={{width:`${pct.toFixed(1)}%`}}/></div>
                      <span className="tc-bar-num">{formatFull(b.taxAmt)}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="tc-row-sub"><td colSpan="2">Base Tax</td><td/><td className="tc-r">{formatFull(rawTax)}</td></tr>
            {rebate > 0 && <tr className="tc-row-rebate"><td colSpan="2">Rebate (Sec 87A)</td><td/><td className="tc-r">– {formatFull(rebate)}</td></tr>}
            {surcharge > 0 && <tr><td colSpan="2">Surcharge</td><td/><td className="tc-r">{formatFull(surcharge)}</td></tr>}
            <tr><td colSpan="2">Health & Education Cess (4%)</td><td/><td className="tc-r">{formatFull(cess)}</td></tr>
            <tr className="tc-row-total"><td colSpan="2">Total Tax Payable</td><td/><td className="tc-r tc-total-val">{formatFull(totalTax)}</td></tr>
          </tbody>
        </table>
      </div>
      <HoverInfoStrip info={hoveredSlab ? getSlabHoverInfo(hoveredSlab, regime) : null} />
    </div>
  );
}

// ─────────────────────────────────────────────
//  REFERENCE DATA
// ─────────────────────────────────────────────
const NEW_REF = [
  ["Up to ₹4,00,000","Nil"],["₹4,00,001 – ₹8,00,000","5%"],["₹8,00,001 – ₹12,00,000","10%"],
  ["₹12,00,001 – ₹16,00,000","15%"],["₹16,00,001 – ₹20,00,000","20%"],
  ["₹20,00,001 – ₹24,00,000","25%"],["Above ₹24,00,000","30%"],
  ["Standard Deduction","₹75,000"],["Rebate 87A","Up to ₹12L = Zero Tax"],
];
const OLD_REF = [
  ["Up to ₹2,50,000","Nil"],["₹2,50,001 – ₹5,00,000","5%"],
  ["₹5,00,001 – ₹10,00,000","20%"],["Above ₹10,00,000","30%"],
  ["Standard Deduction","₹50,000"],["Rebate 87A","Up to ₹5L = Zero Tax"],
  ["Senior (60–79) Exemption","₹3,00,000"],["Super Senior (80+) Exemption","₹5,00,000"],
];

// ─────────────────────────────────────────────
//  STEP INDICATOR
// ─────────────────────────────────────────────
const TABS = [
  { id: "personal",    icon: "👤", label: "Personal",     short: "Step 1" },
  { id: "income",      icon: "💰", label: "Income",        short: "Step 2" },
  { id: "deductions",  icon: "📋", label: "Deductions",    short: "Step 3" },
  { id: "results",     icon: "📊", label: "Results",       short: "Step 4" },
];

function StepTabs({ active, onSelect, resultReady }) {
  return (
    <div className="tc-step-bar">
      {TABS.map((tab, i) => {
        const isLocked = tab.id === "results" && !resultReady;
        const isActive = active === tab.id;
        const isDone   = TABS.findIndex(t => t.id === active) > i;
        return (
          <button
            key={tab.id}
            className={[
              "tc-step-btn",
              isActive  ? "tc-step-btn--active" : "",
              isDone    ? "tc-step-btn--done"   : "",
              isLocked  ? "tc-step-btn--locked" : "",
            ].join(" ")}
            onClick={() => !isLocked && onSelect(tab.id)}
            disabled={isLocked}
          >
            <span className="tc-step-num">{isDone ? "✓" : i + 1}</span>
            <span className="tc-step-icon">{tab.icon}</span>
            <span className="tc-step-label">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────
export default function TaxCalculator() {
  const [salary,      setSalary]      = useState("");
  const [otherIncome, setOtherIncome] = useState(0);
  const [hra,         setHra]         = useState(0);
  const [lta,         setLta]         = useState(0);
  const [sec80c,      setSec80c]      = useState(0);
  const [sec80d,      setSec80d]      = useState(0);
  const [homeLoan,    setHomeLoan]    = useState(0);
  const [nps,         setNps]         = useState(0);
  const [empNps,      setEmpNps]      = useState(0);
  const [otherDed,    setOtherDed]    = useState(0);
  const [age,         setAge]         = useState("below60");
  const [ay,          setAy]          = useState("2026-27");
  const [results,     setResults]     = useState(null);
  const [slabRegime,  setSlabRegime]  = useState("new");
  const [calculating, setCalculating] = useState(false);
  const [activeTab,   setActiveTab]   = useState("personal");

  const sliderVal = Math.min(parseFloat(salary) || 0, 10000000);

  function handleCalculate() {
    const gross = (parseFloat(salary) || 0) + otherIncome;
    if (gross <= 0) { alert("Please enter your annual income first (Step 2)."); return; }
    setCalculating(true);
    setTimeout(() => {
      const nr = computeNew(gross, empNps);
      const or = computeOld(gross, empNps, age, hra, lta, sec80c, sec80d, homeLoan, nps, otherDed);
      setResults({ nr, or, gross });
      setSlabRegime("new");
      setCalculating(false);
      setActiveTab("results");
    }, 650);
  }

  function handleReset() {
    setSalary(""); setOtherIncome(0); setHra(0); setLta(0);
    setSec80c(0); setSec80d(0); setHomeLoan(0); setNps(0);
    setEmpNps(0); setOtherDed(0); setAge("below60"); setAy("2026-27");
    setResults(null); setActiveTab("personal");
  }

  const betterRegime  = results ? (results.nr.totalTax <= results.or.totalTax ? "new" : "old") : null;
  const savings       = results ? Math.abs(results.or.totalTax - results.nr.totalTax) : 0;
  const displayResult = results ? (betterRegime === "new" ? results.nr : results.or) : null;
  const displayTax    = displayResult ? displayResult.totalTax : 0;
  const takeHome      = results ? results.gross - displayTax : 0;
  const effectiveRate = results && results.gross > 0 ? (displayTax / results.gross * 100).toFixed(1) : "0";

  // ── progress indicator for non-results tabs
  const hasIncome     = (parseFloat(salary) || 0) > 0;
  const hasDeductions = sec80c > 0 || sec80d > 0 || homeLoan > 0 || nps > 0 || empNps > 0;

  return (
    <div className="tc-root">

      {/* ── HERO ── */}
      <section className="tc-hero">
        <div className="tc-hero__grid" />
        <div className="tc-hero-inner">
          <span className="tc-badge">⚡ Updated as per Budget 2025</span>
          <h1 className="tc-hero-h1">
            India <span className="tc-hero-accent">Income Tax</span> Calculator 2026
          </h1>
          <p className="tc-hero-p">
            Compare Old vs New Regime, get a detailed slab breakdown, and find your
            best tax-saving option — instantly.
          </p>
          <div className="tc-sources">
            {["ClearTax","Income Tax Dept.","Groww","HDFC Life","IDFC First"].map(s => (
              <span key={s} className="tc-source-chip">{s}</span>
            ))}
          </div>
        </div>
      </section>

      <main className="tc-main">
        <div className="tc-card">

          {/* ── STEP TABS ── */}
          <StepTabs active={activeTab} onSelect={setActiveTab} resultReady={!!results} />

          {/* ── TAB CONTENT ── */}
          <div className="tc-tab-body">

            {/* ════════════════ TAB 1 — PERSONAL ════════════════ */}
            {activeTab === "personal" && (
              <div className="tc-tab-panel" key="personal">
                <div className="tc-tab-header">
                  <div className="tc-tab-title">👤 Personal Information</div>
                  <div className="tc-tab-sub">Your age and assessment year determine your tax exemption limits</div>
                </div>

                <HelpStrip title="Why do we need your age and assessment year?">
                  <p>Your <Tag>Age</Tag> determines your basic exemption limit under the <strong>Old Regime</strong>:</p>
                  <ul>
                    <li><strong>Below 60</strong> — Basic exemption ₹2,50,000</li>
                    <li><strong>Senior (60–79)</strong> — Basic exemption ₹3,00,000</li>
                    <li><strong>Super Senior (80+)</strong> — Basic exemption ₹5,00,000</li>
                  </ul>
                  <p style={{ marginTop: 8 }}>The <Tag>Assessment Year (AY)</Tag> is the year you file your return for income earned in the previous Financial Year.</p>
                  <Tip>Age does <strong>not</strong> affect New Regime slabs — everyone gets the same rates there.</Tip>
                </HelpStrip>

                <div className="tc-grid-2">
                  <Field label="Assessment Year">
                    <select className="tc-select" value={ay} onChange={e => setAy(e.target.value)}>
                      <option value="2026-27">AY 2026-27 (FY 2025-26)</option>
                      <option value="2025-26">AY 2025-26 (FY 2024-25)</option>
                    </select>
                  </Field>
                  <Field label="Age Group" tooltip="Senior Citizens (60–79) and Super Senior Citizens (80+) get higher basic exemptions in Old Regime.">
                    <select className="tc-select" value={age} onChange={e => setAge(e.target.value)}>
                      <option value="below60">Below 60 years</option>
                      <option value="60to79">60 – 79 years (Senior)</option>
                      <option value="80plus">80+ years (Super Senior)</option>
                    </select>
                  </Field>
                </div>

                <div className="tc-tab-nav">
                  <div />
                  <button className="tc-nav-btn tc-nav-btn--next" onClick={() => setActiveTab("income")}>
                    Next: Income Details →
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════ TAB 2 — INCOME ════════════════ */}
            {activeTab === "income" && (
              <div className="tc-tab-panel" key="income">
                <div className="tc-tab-header">
                  <div className="tc-tab-title">💰 Income Details</div>
                  <div className="tc-tab-sub">Enter your annual gross income and any additional income sources</div>
                </div>

                <HelpStrip title="What should I enter as my income?">
                  <p>Enter your <Tag>Annual Gross Salary</Tag> — the <strong>total CTC before any deductions</strong>, including basic pay, HRA, allowances, and bonuses.</p>
                  <Tip>Don't subtract PF or TDS — enter the full gross amount. Deductions are handled in the next step.</Tip>
                </HelpStrip>

                <div className="tc-grid-2">
                  <div className="tc-field tc-field-full">
                    <label className="tc-label">Annual Gross Salary / Income</label>
                    <div className="tc-inp-wrap">
                      <span className="tc-inp-prefix">₹</span>
                      <input type="number" className="tc-inp" value={salary}
                        onChange={e => setSalary(e.target.value)} placeholder="e.g. 1200000" min="0" />
                    </div>
                    <div className="tc-slider-row">
                      <input type="range" className="tc-slider" min="0" max="10000000" step="30010"
                        value={sliderVal}
                        onChange={e => { const v = parseFloat(e.target.value)||0; setSalary(v===0?"":String(v)); }} />
                      <span className="tc-slider-val">{formatINR(sliderVal)}</span>
                    </div>
                  </div>

                  <Field label="Other Income (Interest, FD, etc.)" tooltip="FD interest, savings interest, rental income, capital gains etc.">
                    <RupeeInput id="otherIncome" value={otherIncome} onChange={setOtherIncome} />
                  </Field>
                  <Field label="HRA Exemption Claimed" hint="Old Regime only — not allowed under New Regime">
                    <RupeeInput id="hra" value={hra} onChange={setHra} />
                  </Field>
                  <Field label="Leave Travel Allowance (LTA)" hint="Old Regime only">
                    <RupeeInput id="lta" value={lta} onChange={setLta} />
                  </Field>
                </div>

                <HelpStrip title="How to calculate your HRA and LTA exemption?">
                  <p><Tag>HRA</Tag> exemption = the <strong>lowest</strong> of: actual HRA received, rent paid minus 10% of basic salary, or 50%/40% of basic salary (metro/non-metro).</p>
                  <p style={{ marginTop: 8 }}><Tag>LTA</Tag> covers actual travel costs for 2 domestic trips in a 4-year block.</p>
                  <Tip>Both are <strong>Old Regime only</strong>. The New Regime gives a higher Standard Deduction (₹75K) instead.</Tip>
                </HelpStrip>

                {/* Income preview pill */}
                {hasIncome && (
                  <div className="tc-preview-pill">
                    <span>💰 Total Income:</span>
                    <strong>{formatFull((parseFloat(salary)||0) + otherIncome)}</strong>
                    <span className="tc-preview-note">annual gross</span>
                  </div>
                )}

                <div className="tc-tab-nav">
                  <button className="tc-nav-btn tc-nav-btn--back" onClick={() => setActiveTab("personal")}>
                    ← Back
                  </button>
                  <button className="tc-nav-btn tc-nav-btn--next" onClick={() => setActiveTab("deductions")}>
                    Next: Deductions →
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════ TAB 3 — DEDUCTIONS ════════════════ */}
            {activeTab === "deductions" && (
              <div className="tc-tab-panel" key="deductions">
                <div className="tc-tab-header">
                  <div className="tc-tab-title">📋 Deductions &amp; Investments</div>
                  <div className="tc-tab-sub">
                    These apply to the Old Regime only — skip if you're only comparing
                    <span className="tc-tab-badge">Old Regime Only</span>
                  </div>
                </div>

                <HelpStrip title="How do deductions reduce your tax?">
                  <p>Deductions are subtracted from gross income <strong>before</strong> computing tax. They only apply in the <strong>Old Regime</strong>.</p>
                  <div className="tc-hs-grid2">
                    <div><Tag>80C</Tag> PPF, ELSS, LIC, EPF — max ₹1.5L</div>
                    <div><Tag>80D</Tag> Health insurance premium — max ₹25K/₹50K</div>
                    <div><Tag>Sec 24b</Tag> Home loan interest — max ₹2L</div>
                    <div><Tag>80CCD(1B)</Tag> Extra NPS contribution — max ₹50K</div>
                    <div><Tag>80CCD(2)</Tag> Employer NPS — allowed in both regimes</div>
                    <div><Tag>80G / 80E</Tag> Donations, education loan interest</div>
                  </div>
                  <Tip>Run the calculator even with zero deductions — sometimes the New Regime still wins!</Tip>
                </HelpStrip>

                <div className="tc-grid-3">
                  <Field label="Section 80C (PPF, ELSS, LIC, EPF…)" hint="Max: ₹1,50,000" tooltip="PPF, ELSS mutual funds, life insurance premiums, EPF, home loan principal, tuition fees. Max deduction ₹1,50,000.">
                    <RupeeInput id="sec80c" value={sec80c} onChange={setSec80c} />
                  </Field>
                  <Field label="Section 80D (Health Insurance)" hint="Self: ₹25k | Senior Parents: ₹50k" tooltip="Health insurance for self/spouse/children (₹25K) and/or senior citizen parents (additional ₹50K if 60+).">
                    <RupeeInput id="sec80d" value={sec80d} onChange={setSec80d} />
                  </Field>
                  <Field label="Home Loan Interest (Sec 24b)" hint="Max: ₹2,00,000 (self-occupied)" tooltip="Interest on home loan for self-occupied property. Max ₹2,00,000/year.">
                    <RupeeInput id="homeLoan" value={homeLoan} onChange={setHomeLoan} />
                  </Field>
                  <Field label="NPS Contribution (Sec 80CCD 1B)" hint="Max: ₹50,000 additional" tooltip="Additional deduction of up to ₹50K for NPS Tier 1 contributions by yourself (above the 80C limit).">
                    <RupeeInput id="nps" value={nps} onChange={setNps} />
                  </Field>
                  <Field label="Employer NPS (Sec 80CCD 2)" hint="Allowed in both regimes" tooltip="Employer's NPS contribution — deductible up to 14% of salary (central govt) or 10% (others). Works in New Regime too!">
                    <RupeeInput id="empNps" value={empNps} onChange={setEmpNps} />
                  </Field>
                  <Field label="Other Deductions (80G, 80E, 80TTA…)" tooltip="Charitable donations (80G), education loan interest (80E), savings account interest up to ₹10K (80TTA).">
                    <RupeeInput id="otherDed" value={otherDed} onChange={setOtherDed} />
                  </Field>
                </div>

                <HelpStrip title="💼 Employer NPS is special — works in BOTH regimes">
                  <p><Tag>Employer NPS (80CCD 2)</Tag> is one of the very few deductions available under <strong>both</strong> Old and New Regimes.</p>
                  <Tip>Ask your HR if they can structure part of your CTC as an Employer NPS contribution — it reduces taxable income regardless of which regime you choose!</Tip>
                </HelpStrip>

                {/* Deductions preview */}
                {hasDeductions && (
                  <div className="tc-preview-pill">
                    <span>📋 Total Deductions (Old Regime):</span>
                    <strong>{formatFull(Math.min(sec80c,130010) + Math.min(sec80d, age==="below60"?23001:30010) + Math.min(homeLoan,200000) + Math.min(nps,30010) + empNps + otherDed + hra + lta + 30010)}</strong>
                  </div>
                )}

                {/* Calculate CTA */}
                <div className="tc-calc-cta-row">
                  <button className="tc-nav-btn tc-nav-btn--back" onClick={() => setActiveTab("income")}>
                    ← Back
                  </button>
                  <div className="tc-cta-group">
                    <button className="tc-btn-reset" onClick={handleReset}>↺ Reset All</button>
                    <button className="tc-btn-calc" onClick={handleCalculate} disabled={calculating}>
                      {calculating
                        ? <><span className="tc-spin" /> Calculating…</>
                        : <>⚡ Calculate My Tax</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════ TAB 4 — RESULTS ════════════════ */}
            {activeTab === "results" && results && (
              <div className="tc-tab-panel" key="results">
                <div className="tc-tab-header">
                  <div className="tc-tab-title">📊 Your Tax Results</div>
                  <div className="tc-tab-sub">
                    Based on AY {ay} · {age === "below60" ? "Below 60 yrs" : age === "60to79" ? "Senior (60–79)" : "Super Senior (80+)"}
                    <button className="tc-edit-btn" onClick={() => setActiveTab("personal")}>✏️ Edit Inputs</button>
                  </div>
                </div>

                {/* Summary boxes */}
                <div className="tc-summary-grid">
                  {[
                    { icon:"🏦", label:"Gross Income",       val:formatFull(results.gross),  note:"Annual total",                                      cls:"" },
                    { icon:"💰", label:"Best Tax Payable",   val:formatFull(displayTax),      note:`Under ${betterRegime==="new"?"New":"Old"} Regime`, cls:"orange" },
                    { icon:"✅", label:"Take-Home (Annual)", val:formatFull(takeHome),         note:`${formatFull(takeHome/12)}/month`,                 cls:"green" },
                    { icon:"📊", label:"Effective Rate",     val:`${effectiveRate}%`,          note:"Of gross income",                                  cls:"" },
                  ].map((b,i) => (
                    <div key={i} className="tc-summary-box">
                      <div className="tc-summary-icon">{b.icon}</div>
                      <div className="tc-summary-label">{b.label}</div>
                      <div className={`tc-summary-val ${b.cls}`}>{b.val}</div>
                      <div className="tc-summary-note">{b.note}</div>
                    </div>
                  ))}
                </div>

                {/* Results tabs inside results panel */}
                <div className="tc-results-inner-tabs">
                  {[
                    { id:"compare",  label:"⚖️ Regime Comparison" },
                    { id:"chart",    label:"📈 Breakdown Chart" },
                    { id:"slabs",    label:"🔢 Slab Details" },
                    { id:"reference",label:"📋 Reference" },
                  ].map(rt => (
                    <ResultsInnerTab key={rt.id} tab={rt}
                      results={results} betterRegime={betterRegime} savings={savings}
                      displayResult={displayResult} displayTax={displayTax}
                      slabRegime={slabRegime} setSlabRegime={setSlabRegime}
                    />
                  ))}
                </div>

                <div className="tc-tab-nav tc-tab-nav--results">
                  <button className="tc-btn-reset" onClick={handleReset}>↺ Start Over</button>
                  <button className="tc-nav-btn tc-nav-btn--next" onClick={() => setActiveTab("deductions")}>
                    ← Edit Deductions
                  </button>
                </div>
              </div>
            )}

          </div>{/* /tc-tab-body */}
        </div>{/* /tc-card */}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────
//  RESULTS INNER ACCORDION SECTIONS
// ─────────────────────────────────────────────
function ResultsInnerTab({ tab, results, betterRegime, savings, displayResult, displayTax, slabRegime, setSlabRegime }) {
  const [open, setOpen] = useState(tab.id === "compare");
  const formatFull = n => "₹" + Math.round(n).toLocaleString("en-IN");

  return (
    <div className={`tc-inner-section${open ? " tc-inner-section--open" : ""}`}>
      <button className="tc-inner-header" onClick={() => setOpen(o => !o)}>
        <span>{tab.label}</span>
        <span className={`tc-inner-chevron${open ? " tc-inner-chevron--open" : ""}`}>▼</span>
      </button>

      {open && (
        <div className="tc-inner-body">

          {/* ── COMPARE ── */}
          {tab.id === "compare" && (
            <>
              <HelpStrip title="New Regime vs Old Regime — which should you pick?" defaultOpen={false}>
                <div className="tc-hs-grid2" style={{ marginTop: 8 }}>
                  <div><strong style={{ color: "#263012" }}>🆕 New Regime</strong><br />Lower rates, no investment proof, ₹75K std. deduction. Best if you have few deductions.</div>
                  <div><strong>📋 Old Regime</strong><br />Higher rates but allows 80C, 80D, HRA, home loan, NPS. Best with significant investments/expenses.</div>
                </div>
                <Tip>Rough rule: if your total deductions exceed ₹3.75L, the Old Regime often wins.</Tip>
              </HelpStrip>
              <div className="tc-regime-row">
                <div className={`tc-regime-card${betterRegime==="new"?" best":""}`}>
                  {betterRegime==="new" && <div className="tc-rec">✓ Recommended</div>}
                  <div className="tc-regime-name">🆕 New Regime</div>
                  <div className="tc-regime-tax">{formatFull(results.nr.totalTax)}</div>
                  <div className="tc-regime-note">Taxable: {formatFull(results.nr.taxableIncome)}</div>
                </div>
                <div className="tc-vs">VS</div>
                <div className={`tc-regime-card${betterRegime==="old"?" best":""}`}>
                  {betterRegime==="old" && <div className="tc-rec">✓ Recommended</div>}
                  <div className="tc-regime-name">📋 Old Regime</div>
                  <div className="tc-regime-tax">{formatFull(results.or.totalTax)}</div>
                  <div className="tc-regime-note">Taxable: {formatFull(results.or.taxableIncome)}</div>
                </div>
              </div>
              <div className="tc-savings-strip">
                {savings > 0
                  ? <>🎉 You can <strong>save {formatFull(savings)}</strong> by choosing the <strong>{betterRegime==="new"?"New":"Old"} Regime</strong>!</>
                  : <>⚖️ Both regimes result in the same tax liability.</>}
              </div>
            </>
          )}

          {/* ── CHART ── */}
          {tab.id === "chart" && (
            <>
              <HelpStrip title="Understanding your income breakdown chart">
                <ul>
                  <li><strong style={{ color:"#263012" }}>Green — Take-Home:</strong> What actually reaches your bank after all taxes</li>
                  <li><strong style={{ color:"#263012" }}>Orange — Base Tax:</strong> Income tax as per slab computation</li>
                  <li><strong style={{ color:"#fbbf24" }}>Amber — Cess:</strong> 4% on base tax + surcharge, mandatory for all taxpayers</li>
                  <li><strong style={{ color:"#1d240e" }}>Dark — Surcharge:</strong> Additional levy only if income exceeds ₹50 lakhs</li>
                </ul>
                <Tip>The % in the centre is your effective tax rate — the real fraction of income going to taxes.</Tip>
              </HelpStrip>
              <DonutChart tax={displayTax} gross={results.gross} result={displayResult} />
            </>
          )}

          {/* ── SLABS ── */}
          {tab.id === "slabs" && (
            <>
              <HelpStrip title="How does India's progressive slab system work?">
                <p>Different portions of income are taxed at different rates. Only the amount <em>above</em> each threshold is taxed at the higher rate.</p>
                <Tip>👆 Hover over any row in the table below to see a detailed explanation of that bracket.</Tip>
              </HelpStrip>
              <div className="tc-slab-tabs">
                <button className={`tc-slab-tab${slabRegime==="new"?" on":""}`} onClick={() => setSlabRegime("new")}>New Regime</button>
                <button className={`tc-slab-tab${slabRegime==="old"?" on":""}`} onClick={() => setSlabRegime("old")}>Old Regime</button>
              </div>
              <SlabTable result={slabRegime==="new"?results.nr:results.or} regime={slabRegime} />
              {(slabRegime==="new"?results.nr.rebate:results.or.rebate) > 0 && (
                <div style={{ marginTop: 16 }}>
                  <HelpStrip title="🎉 You qualify for Section 87A Rebate — Zero Tax!" defaultOpen={true}>
                    <ul>
                      <li><strong>New Regime:</strong> Taxable income ≤ ₹12,00,000 → Full tax rebate (zero tax)</li>
                      <li><strong>Old Regime:</strong> Taxable income ≤ ₹5,00,000 → Rebate up to ₹12,500</li>
                    </ul>
                    <Tip>Rebate applies to <strong>taxable income after deductions</strong>, not gross income.</Tip>
                  </HelpStrip>
                </div>
              )}
            </>
          )}

          {/* ── REFERENCE ── */}
          {tab.id === "reference" && (
            <div className="tc-ref-grid">
              {[
                { icon:"🆕", title:"New Regime (Default)",  rows: NEW_REF },
                { icon:"📋", title:"Old Regime (Optional)", rows: OLD_REF },
              ].map(c => (
                <div key={c.title} className="tc-ref-card">
                  <div className="tc-ref-head"><span>{c.icon}</span><strong>{c.title}</strong></div>
                  <table className="tc-ref-table">
                    <tbody>
                      {c.rows.map(([s,r],i) => (
                        <tr key={i}><td>{s}</td><td>{r}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}

        </div>
      )}
    </div>
  );
}